import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  Wifi, 
  Phone, 
  ExternalLink, 
  Filter, 
  Search, 
  MapPin,
  TrendingUp,
  Shield,
  Clock,
  Zap,
  Download,
  Upload,
  Tv,
  Smartphone
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InternetComparatorPage = () => {
  const { t, i18n } = useTranslation();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(i18n.language === 'es' ? 'ES' : 'FR');
  const [speedFilter, setSpeedFilter] = useState('all');

  useEffect(() => {
    fetchProviders();
  }, [selectedCountry]);

  useEffect(() => {
    setSelectedCountry(i18n.language === 'es' ? 'ES' : 'FR');
  }, [i18n.language]);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/providers?country=${selectedCountry}&vertical=BOX`);
      
      // Enrich providers with internet-specific data
      const enrichedProviders = response.data.map(provider => ({
        ...provider,
        speed: getProviderSpeed(provider.name),
        price: getProviderPrice(provider.name, selectedCountry),
        features: getProviderFeatures(provider.name, selectedCountry),
        technology: getProviderTechnology(provider.name)
      }));
      
      setProviders(enrichedProviders);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error(i18n.language === 'es' ? 'Error al cargar proveedores' : 'Erreur lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  const getProviderSpeed = (name) => {
    // Débits actualisés janvier 2026
    const speeds = {
      'Orange': { download: 2000, upload: 800 },      // Livebox Fibre WiFi 7
      'Free': { download: 5000, upload: 800 },        // Freebox Pop
      'SFR': { download: 2000, upload: 800 },         // SFR Fibre Power
      'Bouygues Télécom': { download: 8000, upload: 1000 }, // B&You Pure WiFi 7
      'Movistar': { download: 1000, upload: 300 },    // Fibra 1 Gbps
      'Vodafone': { download: 1000, upload: 300 },    // Vodafone Fibra
      'MásMóvil': { download: 1000, upload: 300 },    // Fibra 1 Gbps
      'O2': { download: 600, upload: 300 },           // O2 Fibra
      'Lowi': { download: 600, upload: 300 },         // Lowi Fibra
      'Digi Mobil': { download: 1000, upload: 500 },  // Digi Fibra 1 Gbps
      'Simyo': { download: 600, upload: 300 }         // Simyo Fibra
    };
    return speeds[name] || { download: 300, upload: 100 };
  };

  const getProviderPrice = (name, country) => {
    // Prix actualisés janvier 2026
    const prices = {
      FR: {
        'Orange': { price: 29.99, originalPrice: 42.99 },     // Livebox Fibre promo
        'Free': { price: 29.99, originalPrice: 39.99 },       // Freebox Pop
        'SFR': { price: 27.99, originalPrice: 42.99 },        // SFR Fibre Power
        'Bouygues Télécom': { price: 23.99, originalPrice: 36.99 } // B&You Pure Fibre WiFi 7
      },
      ES: {
        'Movistar': { price: 39.95, originalPrice: 49.95 },   // Fibra + hausse 2026
        'Vodafone': { price: 25.00, originalPrice: 35.00 },   // Promo 600 Mbps
        'MásMóvil': { price: 29.90, originalPrice: 35.90 },   // Fibra 1 Gbps
        'O2': { price: 34.90, originalPrice: 39.90 },         // O2 Fibra
        'Lowi': { price: 29.95, originalPrice: 34.95 },       // Lowi Fibra 600 Mbps
        'Digi Mobil': { price: 20.00, originalPrice: 25.00 }, // Digi 1 Gbps - le moins cher
        'Simyo': { price: 24.95, originalPrice: 29.95 }       // Simyo Fibra
      }
    };
    return prices[country]?.[name] || { price: 29.99, originalPrice: 35.99 };
  };

  const getProviderFeatures = (name, country) => {
    const commonFeatures = {
      FR: {
        fiber: 'Fibre optique',
        unlimited: 'Illimité',
        tv: 'TV incluse',
        mobile: 'Mobile inclus',
        installation: 'Installation gratuite',
        support: 'Support 24h/7j'
      },
      ES: {
        fiber: 'Fibra óptica',
        unlimited: 'Ilimitado',
        tv: 'TV incluido',
        mobile: 'Móvil incluido',
        installation: 'Instalación gratuita',
        support: 'Soporte 24h/7d'
      }
    };

    const features = commonFeatures[country];
    const providerFeatures = {
      'Orange': ['fiber', 'unlimited', 'tv', 'support'],
      'Free': ['fiber', 'unlimited', 'tv'],
      'SFR': ['fiber', 'unlimited', 'tv', 'mobile'],
      'Bouygues Télécom': ['fiber', 'unlimited', 'installation'],
      'Movistar': ['fiber', 'unlimited', 'tv', 'support'],
      'Vodafone': ['fiber', 'unlimited', 'mobile'],
      'MásMóvil': ['fiber', 'unlimited', 'installation'],
      'O2': ['fiber', 'unlimited'],
      'Lowi': ['fiber', 'unlimited', 'installation'],
      'Digi Mobil': ['fiber', 'unlimited', 'support'],
      'Simyo': ['unlimited', 'installation']
    };

    return (providerFeatures[name] || ['unlimited']).map(key => features[key]);
  };

  const getProviderTechnology = (name) => {
    const fiberProviders = ['Orange', 'Free', 'SFR', 'Bouygues Télécom', 'Movistar', 'Vodafone', 'MásMóvil', 'Lowi', 'Digi Mobil'];
    return fiberProviders.includes(name) ? 'Fibre' : 'ADSL';
  };

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = provider.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSpeed = speedFilter === 'all' || 
      (speedFilter === 'fiber' && provider.technology === 'Fibre') ||
      (speedFilter === 'high' && provider.speed.download >= 500) ||
      (speedFilter === 'medium' && provider.speed.download >= 100 && provider.speed.download < 500);
    
    return matchesSearch && matchesSpeed;
  });

  const callProvider = (phone) => {
    window.open(`tel:${phone}`, '_self');
  };

  const visitWebsite = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const getSpeedLabel = (speed) => {
    if (speed >= 1000) return `${speed / 1000} Gb/s`;
    return `${speed} Mb/s`;
  };

  const getTechnologyBadge = (tech) => {
    if (tech === 'Fibre') {
      return <Badge className="bg-green-100 text-green-800 border-green-200">{tech}</Badge>;
    }
    return <Badge className="bg-orange-100 text-orange-800 border-orange-200">{tech}</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      {/* Hero Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl lg:text-5xl font-bold text-sage-900 mb-4"
              data-testid="internet-comparator-title"
            >
              {t('tools.internet.title')}
            </h1>
            <p className="text-xl text-sage-600 max-w-3xl mx-auto">
              {i18n.language === 'es' 
                ? 'Compara las mejores ofertas de internet y encuentra la conexión perfecta para tu hogar al mejor precio.'
                : 'Comparez les meilleures offres internet et trouvez la connexion parfaite pour votre foyer au meilleur prix.'
              }
            </p>
          </div>

          {/* Search and Filters */}
          <div className="max-w-4xl mx-auto mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sage-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder={i18n.language === 'es' ? 'Buscar proveedor...' : 'Rechercher un fournisseur...'}
                  className="pl-10 border-sage-200 focus:border-sage-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="provider-search-input"
                />
              </div>
              
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className="border-sage-200 focus:border-sage-500">
                  <MapPin className="w-4 h-4 mr-2 text-sage-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FR">
                    <div className="flex items-center gap-2">
                      <span>🇫🇷</span>
                      France
                    </div>
                  </SelectItem>
                  <SelectItem value="ES">
                    <div className="flex items-center gap-2">
                      <span>🇪🇸</span>
                      España
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>

              <Select value={speedFilter} onValueChange={setSpeedFilter}>
                <SelectTrigger className="border-sage-200 focus:border-sage-500">
                  <Zap className="w-4 h-4 mr-2 text-sage-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {i18n.language === 'es' ? 'Todas las velocidades' : 'Toutes vitesses'}
                  </SelectItem>
                  <SelectItem value="fiber">
                    {i18n.language === 'es' ? 'Solo fibra óptica' : 'Fibre optique uniquement'}
                  </SelectItem>
                  <SelectItem value="high">
                    {i18n.language === 'es' ? 'Muy alta velocidad (+500 Mb/s)' : 'Très haut débit (+500 Mb/s)'}
                  </SelectItem>
                  <SelectItem value="medium">
                    {i18n.language === 'es' ? 'Alta velocidad (100-500 Mb/s)' : 'Haut débit (100-500 Mb/s)'}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Info Banner */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                <Wifi className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">
                  {i18n.language === 'es' ? 'Comparación actualizada' : 'Comparaison actualisée'}
                </h3>
                <p className="text-blue-700 text-sm">
                  {i18n.language === 'es' 
                    ? 'Precios y ofertas actualizadas semanalmente. Incluye promociones y descuentos vigentes.'
                    : 'Prix et offres mis à jour hebdomadairement. Inclut promotions et réductions en cours.'
                  }
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-blue-600">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {i18n.language === 'es' ? 'Act. semanal' : 'Maj. hebdo'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Providers Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-sage-900">
                {i18n.language === 'es' ? 'Proveedores de Internet' : 'Fournisseurs Internet'} {selectedCountry === 'FR' ? '🇫🇷' : '🇪🇸'}
              </h2>
              <p className="text-sage-600">
                {filteredProviders.length} {i18n.language === 'es' ? 'proveedor' : 'fournisseur'}{filteredProviders.length > 1 ? (i18n.language === 'es' ? 'es' : 's') : ''} {i18n.language === 'es' ? 'encontrado' : 'trouvé'}{filteredProviders.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-lg h-96"></div>
                </div>
              ))}
            </div>
          ) : filteredProviders.length === 0 ? (
            <div className="text-center py-16">
              <Wifi className="w-16 h-16 text-sage-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-sage-700 mb-2">
                {i18n.language === 'es' ? 'No se encontraron proveedores' : 'Aucun fournisseur trouvé'}
              </h3>
              <p className="text-sage-500">
                {i18n.language === 'es' ? 'Intenta modificar tus criterios de búsqueda' : 'Essayez de modifier vos critères de recherche'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProviders.map((provider) => {
                const pricing = provider.price || { price: 29.99, originalPrice: 35.99 };
                const savings = pricing.originalPrice - pricing.price;
                
                return (
                  <Card 
                    key={provider.id}
                    className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-lg overflow-hidden bg-white relative"
                    data-testid={`provider-card-${provider.id}`}
                  >
                    {/* Savings Badge */}
                    {savings > 0 && (
                      <div className="absolute top-4 right-4 z-10">
                        <Badge className="bg-red-500 text-white">
                          -{savings.toFixed(0)}€/{i18n.language === 'es' ? 'mes' : 'mois'}
                        </Badge>
                      </div>
                    )}

                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="w-16 h-16 rounded-xl overflow-hidden mb-3 bg-gray-100 flex items-center justify-center">
                            {provider.logo_url ? (
                              <img 
                                src={provider.logo_url} 
                                alt={`${provider.name} logo`}
                                className="max-w-full max-h-full object-contain"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div 
                              className="w-full h-full flex items-center justify-center text-white font-bold text-sm"
                              style={{ backgroundColor: provider.primary_color, display: provider.logo_url ? 'none' : 'flex' }}
                            >
                              <Wifi className="w-8 h-8" />
                            </div>
                          </div>
                          
                          <CardTitle className="text-xl font-bold text-sage-900 mb-2">
                            {provider.name}
                          </CardTitle>
                          
                          <div className="flex items-center gap-2 mb-3">
                            {getTechnologyBadge(provider.technology)}
                            <Badge variant="outline" className="text-xs">
                              {provider.country === 'FR' ? 'France' : 'España'}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Speed Display */}
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 mb-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                              <Download className="w-4 h-4 text-green-600" />
                              <span className="text-xs text-gray-600">
                                {i18n.language === 'es' ? 'Descarga' : 'Téléchargement'}
                              </span>
                            </div>
                            <div className="font-bold text-green-600">
                              {getSpeedLabel(provider.speed.download)}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                              <Upload className="w-4 h-4 text-blue-600" />
                              <span className="text-xs text-gray-600">
                                {i18n.language === 'es' ? 'Subida' : 'Envoi'}
                              </span>
                            </div>
                            <div className="font-bold text-blue-600">
                              {getSpeedLabel(provider.speed.upload)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Features */}
                      <div className="space-y-2 mb-4">
                        {provider.features.slice(0, 3).map((feature, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm text-sage-600">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            {feature}
                          </div>
                        ))}
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0">
                      {/* Pricing */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="text-3xl font-bold text-sage-900">
                          {pricing.price}€
                        </div>
                        <div className="flex flex-col">
                          <div className="text-sm text-gray-400 line-through">
                            {pricing.originalPrice}€
                          </div>
                          <div className="text-xs text-sage-600">
                            /{i18n.language === 'es' ? 'mes' : 'mois'}
                          </div>
                        </div>
                      </div>

                      <Separator className="mb-4" />
                      
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Button 
                            onClick={() => callProvider(provider.phone)}
                            className="flex-1 text-sm"
                            style={{ backgroundColor: provider.primary_color }}
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            {t('common.call_free')}
                          </Button>
                          
                          {provider.website_url && (
                            <Button 
                              variant="outline" 
                              onClick={() => visitWebsite(provider.website_url)}
                              className="border-sage-200 text-sage-600 hover:bg-sage-50"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600 max-w-4xl mx-auto">
              <strong>{i18n.language === 'es' ? 'Aviso:' : 'Avertissement :'}</strong> {i18n.language === 'es' 
                ? 'Las tarifas mostradas son orientativas y pueden variar. Para obtener un presupuesto personalizado, contacte con nuestros asesores. Servicio gratuito - Llamada no recargada.'
                : 'Les tarifs affichés sont fournis à titre indicatif et peuvent varier. Pour obtenir un devis personnalisé, contactez nos conseillers. Service gratuit - Appel non surtaxé.'
              }
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};